import FormGroup from "@components/formGroup/FormGroup";
import { getInfoValue } from "@components/smart/FieldInfo";
import { getTooltip } from "@components/smart/smartField/SmartFieldUtils";
import { IFormGroupDef, TFormTable } from "@components/smart/smartFormGroup/SmartFormGroup";
import SmartSimpleTable from "@components/smart/smartSimpleTable/SmartSimpleTable";
import { SmartTable } from "@components/smart/smartTable/SmartTable";
import { TableWithEndpoint } from "@components/table/TableWithEndpoint";
import BindingContext, { createBindingContext } from "@odata/BindingContext";
import memoizeOneWithParamsCheck from "@utils/memoizeOne";
import React from "react";

import { FormTableWrapper } from "../table/TableView.styles";
import { getFormTableFilterQuery } from "./Form.utils";
import { FormStorage } from "./FormStorage";

export const renderFormTable = memoizeOneWithParamsCheck((storage: FormStorage,
                                groupDef: IFormGroupDef, tableDef: TFormTable, keyBindingContext: BindingContext, isInTab: boolean, key: string, topGroupId: string) => {
    /**
     * In case storage data binding context is new, we don't want to render the table at all,
     * because the filter won't be valid. If there will be a need to render the table in this case,
     * move the visibility condition from common "Tabs" group to all specific table groups, because the inner table
     * group may be re-rendered by resize handler of SmartFormTabs and it doesn't go through the visibility check
     * of the Tabs group. See https://solitea-cz.atlassian.net/browse/DEV-28150
     */
    const isNew = storage.data.bindingContext.isNew();
    // in case storage is busy, we don't want to render the table at all -> may be some action is in progress, so
    // we should not to fire additional requests if the properties might not be valid or incomplete.
    const isBusy = storage.isBusy();
    let table;

    if (isNew || isBusy) {
        return null;
    }

    if (tableDef.endPoint) {
        const url = `${tableDef.endPoint}/${storage.data.bindingContext.getKey()}?CompanyId=${storage.context.getCompanyId()}`;
        table = (
            <TableWithEndpoint endPoint={url} storage={storage} columns={tableDef.columns}/>
        );
    }

    if (tableDef.entitySet) {
        const groupStatus = storage.getGroupStatus(topGroupId ?? groupDef.id);
        const columns = groupStatus.tabColumns?.[groupDef.id] ?? tableDef.columns;

        const bindingContext = createBindingContext(tableDef.entitySet, storage.oData.getMetadata());
        const filterQuery = getFormTableFilterQuery(storage, tableDef, storage.data.bindingContext);
        table = (
            <FormTableWrapper isInTab={isInTab}>
                {tableDef.isSimplified ? (
                    <SmartSimpleTable key={key}
                                      bindingContext={bindingContext}
                                      columns={columns}
                                      storage={storage}
                                      filter={filterQuery}
                                      initialSortBy={tableDef.initialSortBy}
                                      hasBigFont
                                      highlightRowsHover={false}
                                      showHeaderBorder={false}
                                      pageSize={50}/>
                ) : (
                    <SmartTable key={key}
                                bindingContext={bindingContext}
                                initialSortBy={tableDef.initialSortBy}
                                columns={columns}
                                storage={storage as any} // TODO: FormStorage is passed as TSmartODataTableStorage, if it's ok, change SmartTable to accept FormStorage
                                filter={filterQuery}
                                tableId={tableDef.id}
                                disableVirtualization
                                preventAutoReset={tableDef.preventAutoReset}
                                hideDrilldown={true}/>
                )}
            </FormTableWrapper>
        );
    }

    if (!table) {
        return null;
    }

    return isInTab ? (
        <React.Fragment key={key}>
            {table}
        </React.Fragment>
    ) : (
        <FormGroup
            key={key}
            id={groupDef.id}
            title={getInfoValue(groupDef, "title", { storage })}
            tooltip={getTooltip(groupDef.tooltip, storage)}
            showLine={groupDef.showGroupDivider ?? true}
            isSmallBottomMargin={groupDef.isSmallBottomMargin}>
            {table}
        </FormGroup>
    );
});